import { useQuery } from '@apollo/client';
import { createContext, useContext, useMemo } from 'react';

import {
  Retailer,
  ThemeColorScheme,
} from '@rebound/shared-models/retailer/retailer.model';

import {
  formatInformationCards,
  formatNestedReturnReasons,
  translationOptionsToMap,
} from '../../../../shared-models/retailer/retailer.utils';
import {
  GetRetailerBySubdomainQuery,
  GetRetailerBySubdomainQueryResult,
} from './retailer.queries';

export const RetailerContext = createContext({ subdomain: '' });

export const useRetailerData = () => {
  const subdomainData = useContext(RetailerContext);

  const { data, loading } = useQuery<GetRetailerBySubdomainQueryResult>(
    GetRetailerBySubdomainQuery,
    {
      variables: { subdomain: subdomainData.subdomain },
    },
  );

  const retailer = useMemo(() => {
    if (!data?.getRetailerBySubdomain) {
      return undefined;
    }

    return {
      ...data.getRetailerBySubdomain,
      nestedReturnReasons: formatNestedReturnReasons(
        data.getRetailerBySubdomain.nestedReturnReasons,
      ),
      informationCards: formatInformationCards(
        data.getRetailerBySubdomain.informationCards,
      ),
      confirmationTextTranslations: translationOptionsToMap(
        data.getRetailerBySubdomain.confirmationTextTranslations,
      ),
    } as Retailer;
  }, [data]);

  return { retailer, loading };
};

export const useRetailerSubdomain = () => {
  const subdomainData = useContext(RetailerContext);

  return subdomainData.subdomain;
};

export const useRetailerColorScheme = () => {
  const subdomainData = useContext(RetailerContext);

  const { data } = useQuery<GetRetailerBySubdomainQueryResult>(
    GetRetailerBySubdomainQuery,
    {
      variables: { subdomain: subdomainData.subdomain },
    },
  );

  return (
    data?.getRetailerBySubdomain.theme.colorScheme || ThemeColorScheme.Indigo
  );
};

export const useRetailerJourney = () => {
  const subdomainData = useContext(RetailerContext);

  const { data } = useQuery<GetRetailerBySubdomainQueryResult>(
    GetRetailerBySubdomainQuery,
    {
      variables: { subdomain: subdomainData.subdomain },
    },
  );

  return data?.getRetailerBySubdomain.userJourneyRadio;
};
